<script module>
  export const i18n = {
    en: {},
    de: {
      'Must be after %s': 'Muss nach dem %s liegen',
      'Must be before %s': 'Muss vor dem %s liegen',
      'Invalid date': 'Ungültiges Datum'
    },
    nb: {
      'Must be after %s': 'Må være etter %s',
      'Must be before %s': 'Må være før %s',
      'Invalid date': 'Ugyldig dato'
    },
    sv: {
      'Must be after %s': 'Måste vara efter %s',
      'Must be before %s': 'Måste vara före %s',
      'Invalid date': 'Ogiltigt datum'
    },
    da: {
      'Must be after %s': 'Måste være efter %s',
      'Must be before %s': 'Måste være før %s',
      'Invalid date': 'Ugyldigt dato'
    }
  }
</script>

<script>
  import { addDays } from 'date-fns'

  import { register } from '$lib/utils/text.svelte.js'
  import Symbol from '$lib/Symbol.svelte'
  import Input from '$lib/Input.svelte'

  const MIN_BIRTHDATE_LENGTH = 4

  const DATE_FORMAT =
    /^(?:\d{4}([^\d])\d{1,2}\1\d{1,2})|(?:\d{1,2}([^\d])\d{1,2}\2\d{4})$/

  /** @param {string|Date|null} [input] */
  const parse = (input) => {
    if (
      !input ||
      (typeof input === 'string' && input.length < MIN_BIRTHDATE_LENGTH)
    ) {
      return
    }

    // Manually parse dates in the format DD/MM?/YYYY to prevent crazy US dates
    if (
      typeof input === 'string' &&
      /^\d{2}([^\d])\d{1,2}\1\d{4}$/.test(input)
    ) {
      const [day, month, year] = input.split(/[^\d]/)
      return new Date(+year, +month - 1, +day)
    }

    let date = new Date(input)

    if (!isNaN(date.getTime())) return date
  }

  /** @param {Date} date */
  const format = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  /** @param {Date?} [date] */
  const getErrorMessage = (date) => {
    if (!date) return text`Invalid date`
    if (min && new Date(min) > date) {
      return text`Must be after ${toLocaleDateString(addDays(min, -1))}`
    }
    if (max && new Date(max) < date) {
      return text`Must be before ${toLocaleDateString(addDays(max, 1))}`
    }
  }

  const onvaluechange = (input) => {
    intermediateValue = input
    if (!input) {
      message = ''
      return
    }
    const date = parse(intermediateValue)
    message = getErrorMessage(date) || ''
    if (message) {
      valid = false
    } else {
      message = ''
      valid = true
      value = format(/** @type {Date} */ (date))
      onchange?.(value)
    }
  }

  const onblur = (event) => {
    if (attrs.onblur) attrs.onblur(event)
    clearTimeout(timeout)
    onvaluechange(event.target.value)
  }

  let timeout
  const oninput = (event) => {
    clearTimeout(timeout)
    if (attrs.oninput) attrs.oninput()
    if (DATE_FORMAT.test(event.target.value)) {
      onvaluechange(event.target.value)
    } else {
      timeout = setTimeout(onvaluechange, 1750, event.target.value)
    }
  }

  const onkeydown = (event) => {
    if (attrs.onkeydown) attrs.onkeydown(event)
    if (event.key === 'Enter') {
      onvaluechange(event.target.value)
      event.preventDefault()
    }
  }

  /** @param {string|Date} value */
  const toLocaleDateString = (value) => {
    const date = parse(value)
    return date
      ? date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      : ''
  }

  let text = register(i18n)

  /** @type {{
   * id: string,
   * min?: string,
   * max?: string,
   * value?: string?,
   * label?: string | Svelte.Snippet,
   * onchange?: function(string): void,
   * action?: import('svelte/action').Action
   * [key: string]: any
  }} */
  let {
    id,
    min,
    max,
    label,
    action,
    onchange,
    value = $bindable(''),
    ...attrs
  } = $props()

  let intermediateValue = $state(value)
  let parsed = $derived(parse(intermediateValue))

  const initialMessage = value ? getErrorMessage(parse(value)) || '' : ''
  let message = $state(initialMessage)
  let valid = $state(!initialMessage)
</script>

<Input
  {...attrs}
  aria-labelledby={message
    ? `${id}-dateinput-message ${attrs['aria-labelledby'] || ''}`
    : attrs['aria-labelledby']}
  pattern="\d&lbrace;4&rbrace;-\d&lbrace;2&rbrace;-\d&lbrace;2&rbrace;"
  {id}
  {value}
  {label}
  {action}
  {onblur}
  {oninput}
  {onkeydown}>
  {#snippet output()}
    {#if message}
      <span aria-label="{id}-dateinput-message">{message}</span>
    {:else if parsed}
      <output for={id}>
        {toLocaleDateString(parsed)}
      </output>
    {/if}
    {#if parsed || message}
      <span class="indicator" class:valid>
        <Symbol circle suffix size="xs" name={valid ? 'checkmark' : 'cross'} />
      </span>
    {/if}
  {/snippet}
</Input>

<style>
  .indicator {
    --current-background: rgb(var(--rgb-red));
    --current-color: rgb(var(--rgb-white));

    transform: translateY(0.0625rem);
  }

  .indicator.valid {
    --current-background: rgb(var(--rgb-green));
  }
</style>
